import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAlerts, getDashboardStats, getSalesOverTime, getTopProducts, getPaymentMethods, getRecentActivities } from '../services/api';
import Layout from '../components/Layout';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler);

const Dashboard = () => {
    const [alerts, setAlerts] = useState(null);
    const [stats, setStats] = useState(null);
    const [recentActivities, setRecentActivities] = useState([]);
    const [salesChartData, setSalesChartData] = useState(null);
    const [topProductsData, setTopProductsData] = useState(null);
    const [paymentMethodsData, setPaymentMethodsData] = useState(null);
    const [salesPeriod, setSalesPeriod] = useState(7);
    const [user] = useState(JSON.parse(localStorage.getItem('user')));

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [alertsRes, statsRes, productsRes, paymentsRes, activitiesRes] = await Promise.all([
                    getAlerts(),
                    getDashboardStats(),
                    getTopProducts(),
                    getPaymentMethods(),
                    getRecentActivities()
                ]);
                
                setAlerts(alertsRes.data);
                setStats(statsRes.data);
                formatTopProducts(productsRes.data);
                formatPaymentMethods(paymentsRes.data);
                setRecentActivities(activitiesRes.data);
            } catch (err) {
                console.error("Dashboard data fetch error:", err);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        getSalesOverTime(salesPeriod).then(res => formatSalesChart(res.data)).catch(err => console.error(err));
    }, [salesPeriod]);

    const formatSalesChart = (data) => {
        setSalesChartData({
            labels: data.map(d => new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
            datasets: [{
                label: 'Revenue',
                data: data.map(d => d.total_sales),
                borderColor: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderWidth: 3,
                pointBackgroundColor: '#ffffff',
                pointBorderColor: 'rgba(255, 255, 255, 0.5)',
                pointHoverRadius: 6,
                fill: true,
                tension: 0.4,
            }]
        });
    };

    const formatTopProducts = (data) => {
        setTopProductsData({
            labels: data.map(p => p.product_name),
            datasets: [{
                label: 'Units Sold',
                data: data.map(p => p.total_quantity_sold),
                backgroundColor: ['#6C5DD3', '#FF754C', '#00D09C', '#3F8CFF', '#FFB800'],
                borderRadius: 8,
                barThickness: 20
            }]
        });
    };

    const formatPaymentMethods = (data) => {
        setPaymentMethodsData({
            labels: data.map(p => p.payment_method.charAt(0).toUpperCase() + p.payment_method.slice(1)),
            datasets: [{
                label: 'Total Amount',
                data: data.map(p => p.total_amount),
                backgroundColor: ['#6C5DD3', '#FF754C', '#00D09C'],
                borderRadius: 8,
            }]
        });
    };

    const getTimeGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good Morning';
        if (hour < 18) return 'Good Afternoon';
        return 'Good Evening';
    };

    return (
        <Layout title="Dashboard Overview">
            {/* Welcome Banner */}
            <div className="dashboard-welcome d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                <div className="mb-3 mb-md-0">
                    <h1 className="fw-bold mb-1">{getTimeGreeting()}, {user?.name.split(' ')[0]}! 👋</h1>
                    <p className="mb-0 opacity-75">Here is what's happening with your shop today.</p>
                </div>
                <div className="d-flex gap-2 dashboard-welcome-stats">
                    <div className="welcome-stat-box" style={{minWidth: '120px'}}>
                        <small className="d-block opacity-75 mb-1 text-white">Today's Sales</small>
                        <h4 className="fw-bold mb-0 text-white">Rs. {stats?.today_sales.amount || '0'}</h4>
                    </div>
                    <div className="welcome-stat-box" style={{minWidth: '120px'}}>
                        <small className="d-block opacity-75 mb-1 text-white">Total Orders</small>
                        <h4 className="fw-bold mb-0 text-white">{recentActivities.length}+</h4>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="row g-3 mb-4">
                <div className="col-6 col-md-3">
                    <Link to="/pos" className="quick-action-card">
                        <div className="action-icon"><i className="bi bi-cart-plus-fill"></i></div>
                        <span className="action-text">New Sale</span>
                    </Link>
                </div>
                <div className="col-6 col-md-3">
                    <Link to="/inventory" className="quick-action-card">
                        <div className="action-icon"><i className="bi bi-box-seam-fill"></i></div>
                        <span className="action-text">Add Product</span>
                    </Link>
                </div>
                <div className="col-6 col-md-3">
                    <Link to="/customers" className="quick-action-card">
                        <div className="action-icon"><i className="bi bi-person-plus-fill"></i></div>
                        <span className="action-text">New Customer</span>
                    </Link>
                </div>
                <div className="col-6 col-md-3">
                    <Link to="/reports" className="quick-action-card">
                        <div className="action-icon"><i className="bi bi-file-earmark-bar-graph-fill"></i></div>
                        <span className="action-text">Reports</span>
                    </Link>
                </div>
            </div>

            {/* Modern Stats Row */}
            <div className="row g-4 mb-4">
                <div className="col-sm-6 col-xl-3">
                    <div className="stat-card-modern violet">
                        <div className="stat-label">Daily Revenue</div>
                        <div className="stat-value">Rs. {stats?.today_sales.amount || '0'}</div>
                        <div className="small opacity-75">
                            <i className={`bi bi-arrow-${stats?.today_sales.percentage_change >= 0 ? 'up' : 'down'}`}></i> {Math.abs(stats?.today_sales.percentage_change || 0)}% from yesterday
                        </div>
                        <i className="bi bi-currency-dollar stat-icon-bg"></i>
                    </div>
                </div>
                <div className="col-sm-6 col-xl-3">
                    <div className="stat-card-modern blue">
                        <div className="stat-label">Net Profit (30d)</div>
                        <div className="stat-value">Rs. {stats?.profit_snapshot.profit || '0'}</div>
                        <div className="small opacity-75">Margin: {stats?.profit_snapshot.margin}%</div>
                        <i className="bi bi-graph-up stat-icon-bg"></i>
                    </div>
                </div>
                <div className="col-sm-6 col-xl-3">
                    <div className="stat-card-modern orange">
                        <div className="stat-label">Inventory Items</div>
                        <div className="stat-value">{stats?.inventory_stats.total_products || '0'}</div>
                        <div className="small opacity-75">{alerts?.low_stock.length} items low in stock</div>
                        <i className="bi bi-box-seam stat-icon-bg"></i>
                    </div>
                </div>
                <div className="col-sm-6 col-xl-3">
                    <div className="stat-card-modern emerald">
                        <div className="stat-label">Total Customers</div>
                        <div className="stat-value">{stats?.inventory_stats.total_customers || '0'}</div>
                        <div className="small opacity-75">Active regular buyers</div>
                        <i className="bi bi-people stat-icon-bg"></i>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                {/* Main Sales Chart */}
                <div className="col-lg-12 col-xl-8">
                    <div className="card h-100 border-0 shadow-sm overflow-hidden" style={{borderRadius: '24px'}}>
                        <div className="card-header bg-dark p-4 border-0 d-flex justify-content-between align-items-center">
                            <div>
                                <h5 className="text-white fw-bold mb-0">Revenue Velocity</h5>
                                <small className="text-white opacity-50">Visualizing sales trends</small>
                            </div>
                            <div className="btn-group bg-white bg-opacity-10 p-1 rounded-3">
                                <button className={`btn btn-sm text-white ${salesPeriod === 7 ? 'bg-white bg-opacity-25' : ''}`} onClick={() => setSalesPeriod(7)}>7D</button>
                                <button className={`btn btn-sm text-white ${salesPeriod === 30 ? 'bg-white bg-opacity-25' : ''}`} onClick={() => setSalesPeriod(30)}>30D</button>
                            </div>
                        </div>
                        <div className="card-body bg-dark pt-0 pb-4 px-2" style={{height: '320px'}}>
                            {salesChartData ? <Line data={salesChartData} options={{ 
                                responsive: true, 
                                maintainAspectRatio: false,
                                plugins: { legend: { display: false } },
                                scales: { 
                                    y: { grid: { display: false }, ticks: { color: 'rgba(255,255,255,0.5)' } },
                                    x: { grid: { display: false }, ticks: { color: 'rgba(255,255,255,0.5)' } }
                                }
                            }} /> : <div className="d-flex justify-content-center align-items-center h-100"><div className="spinner-border text-light"></div></div>}
                        </div>
                    </div>
                </div>

                {/* Recent Activities */}
                <div className="col-lg-12 col-xl-4">
                    <div className="card h-100 border-0 shadow-sm" style={{borderRadius: '24px'}}>
                        <div className="card-header bg-transparent border-0 p-4">
                            <h5 className="fw-bold mb-0">Recent Sales</h5>
                        </div>
                        <div className="card-body pt-0 px-4 activity-feed">
                            {recentActivities.length > 0 ? (
                                recentActivities.map(activity => (
                                    <div key={activity.id} className="activity-item">
                                        <div className={`activity-indicator bg-${activity.payment_status === 'paid' ? 'success' : 'warning'}`}></div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between">
                                                <h6 className="mb-0 fw-bold">{activity.customer_name || 'Walk-in Customer'}</h6>
                                                <small className="fw-bold text-primary">Rs. {activity.final_amount}</small>
                                            </div>
                                            <small className="text-muted d-block">{new Date(activity.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} • #{activity.invoice_number}</small>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-5">
                                    <i className="bi bi-clock-history fs-1 text-muted opacity-25"></i>
                                    <p className="text-muted mt-2">No recent activities</p>
                                </div>
                            )}
                            <Link to="/reports" className="btn btn-light w-100 mt-3 fw-bold py-2 rounded-3">View All Transactions</Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                {/* Top Products */}
                <div className="col-sm-6 col-xl-4">
                    <div className="card border-0 shadow-sm h-100" style={{borderRadius: '24px'}}>
                        <div className="card-header bg-transparent border-0 p-4 pb-0">
                            <h5 className="fw-bold mb-0">Top Products</h5>
                        </div>
                        <div className="card-body p-4" style={{height: '300px'}}>
                            {topProductsData ? <Bar data={topProductsData} options={{ 
                                responsive: true, 
                                maintainAspectRatio: false, 
                                indexAxis: 'y',
                                plugins: { legend: { display: false } },
                                scales: { x: { grid: { display: false } }, y: { grid: { display: false } } }
                            }} /> : <div className="d-flex justify-content-center align-items-center h-100"><div className="spinner-border text-primary"></div></div>}
                        </div>
                    </div>
                </div>

                {/* Payment Methods */}
                <div className="col-sm-6 col-xl-4">
                    <div className="card border-0 shadow-sm h-100" style={{borderRadius: '24px'}}>
                        <div className="card-header bg-transparent border-0 p-4 pb-0">
                            <h5 className="fw-bold mb-0">Payment Channels</h5>
                        </div>
                        <div className="card-body p-4" style={{height: '300px'}}>
                            {paymentMethodsData ? <Bar data={paymentMethodsData} options={{ 
                                responsive: true, 
                                maintainAspectRatio: false, 
                                plugins: { legend: { display: false } },
                                scales: { x: { grid: { display: false } }, y: { grid: { display: false } } }
                            }} /> : <div className="d-flex justify-content-center align-items-center h-100"><div className="spinner-border text-primary"></div></div>}
                        </div>
                    </div>
                </div>

                {/* Alerts / Stock */}
                <div className="col-xl-4">
                    <div className="card border-0 shadow-sm h-100" style={{borderRadius: '24px'}}>
                        <div className="card-header bg-transparent border-0 p-4 pb-0">
                            <h5 className="fw-bold mb-0 text-danger">Inventory Alerts</h5>
                        </div>
                        <div className="card-body p-4">
                             {alerts && (alerts.low_stock.length > 0 || alerts.expiring_soon.length > 0) ? (
                                <>
                                    {alerts.low_stock.slice(0, 2).map(p => (
                                        <div key={p.id} className="d-flex align-items-center gap-2 mb-2 p-2 bg-danger bg-opacity-10 rounded-3">
                                            <div className="flex-grow-1 min-w-0">
                                                <h6 className="mb-0 fw-bold text-truncate">{p.name}</h6>
                                                <small className="text-danger">Stock: {p.quantity}</small>
                                            </div>
                                            <Link to="/inventory" className="btn btn-xs btn-danger rounded-pill">Stock</Link>
                                        </div>
                                    ))}
                                    {alerts.expiring_soon.slice(0, 2).map(p => (
                                        <div key={p.id} className="d-flex align-items-center gap-2 mb-2 p-2 bg-warning bg-opacity-10 rounded-3">
                                            <div className="flex-grow-1 min-w-0">
                                                <h6 className="mb-0 fw-bold text-truncate">{p.name}</h6>
                                                <small className="text-warning-emphasis">Expires: {new Date(p.expiry_date).toLocaleDateString()}</small>
                                            </div>
                                            <Link to="/inventory" className="btn btn-xs btn-warning rounded-pill">View</Link>
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="bi bi-check-circle-fill text-success fs-1"></i>
                                    <p className="text-muted mt-2">All systems healthy</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Dashboard;
